import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Link2,
  QrCode,
  FileText,
  ArrowUpRight,
  MousePointerClick,
  Users,
  TrendingUp,
} from 'lucide-react';
import { formatNumber } from '@/client/lib/utils';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/client/components/ui/card';
import { Button } from '@/client/components/ui/button';

interface TopPerformer {
  resourceId: string;
  resourceName: string;
  resourceType: 'link' | 'qr' | 'page';
  interactions: number;
  uniqueVisitors: number;
  conversions: number;
  projectId: string | null;
  projectName: string | null;
}

interface ResourcePerformanceTableProps {
  links: TopPerformer[];
  qrCodes: TopPerformer[];
  pages: TopPerformer[];
}

type Tab = 'link' | 'qr' | 'page';

export function ResourcePerformanceTable({ links, qrCodes, pages }: ResourcePerformanceTableProps) {
  const [activeTab, setActiveTab] = useState<Tab>('link');

  const data = activeTab === 'link' ? links : activeTab === 'qr' ? qrCodes : pages;
  const hasData = data && data.length > 0;

  const getMetricLabel = (tab: Tab) => {
    switch (tab) {
      case 'link':
        return 'Clicks';
      case 'qr':
        return 'Scans';
      case 'page':
        return 'Views';
    }
  };

  const getLinkPrefix = (tab: Tab) => {
    switch (tab) {
      case 'link':
        return '/links';
      case 'qr':
        return '/qr-codes';
      case 'page':
        return '/landing-pages';
    }
  };

  return (
    <Card className="rounded-2xl py-0">
      <CardHeader className="pb-2 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Resource Performance</CardTitle>
            <CardDescription>Top performing assets by engagement</CardDescription>
          </div>
          <div className="flex items-center gap-1 bg-background rounded-lg p-1 border">
            <Button
              variant={activeTab === 'link' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('link')}
              className="h-7 gap-1.5 text-xs"
            >
              <Link2 className="size-3.5" />
              Links
            </Button>
            <Button
              variant={activeTab === 'qr' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('qr')}
              className="h-7 gap-1.5 text-xs"
            >
              <QrCode className="size-3.5" />
              QR Codes
            </Button>
            <Button
              variant={activeTab === 'page' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('page')}
              className="h-7 gap-1.5 text-xs"
            >
              <FileText className="size-3.5" />
              Pages
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!hasData ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <p>No data available for this resource type</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/10">
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground">Name</th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground w-24">
                    <div className="flex items-center justify-end gap-1">
                      <MousePointerClick className="size-3.5" />
                      {getMetricLabel(activeTab)}
                    </div>
                  </th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground w-24">
                    <div className="flex items-center justify-end gap-1">
                      <Users className="size-3.5" />
                      Visitors
                    </div>
                  </th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground w-24">
                    <div className="flex items-center justify-end gap-1">
                      <TrendingUp className="size-3.5" />
                      Conv.
                    </div>
                  </th>
                  <th className="h-10 px-4 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((item) => (
                  <tr key={item.resourceId} className="group hover:bg-muted/30 transition-colors">
                    <td className="p-4">
                      <div className="font-medium truncate max-w-[200px] sm:max-w-[300px]">
                        {item.resourceName}
                      </div>
                      {item.projectName && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {item.projectName}
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right tabular-nums">
                      {formatNumber(item.interactions)}
                    </td>
                    <td className="p-4 text-right tabular-nums text-muted-foreground">
                      {formatNumber(item.uniqueVisitors)}
                    </td>
                    <td className="p-4 text-right tabular-nums text-muted-foreground">
                      {formatNumber(item.conversions)}
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        asChild
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Link to={`${getLinkPrefix(activeTab)}/${item.resourceId}`}>
                          <ArrowUpRight className="size-4" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
